import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { Topic, TopicSubject } from '~src/interfaces/topic.interface';
import { QuestionSubmit } from '~src/modules/topics/actions/topicActions';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import logger from '~src/services/logger/LlsLogger';
import { translateContent } from '~src/services/translationServices';
import {
  validatorMaxLengthLink1000,
  validatorMaxLengthLink500,
  validatorRequired,
} from '~src/services/validators';
import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import TranslatableSectionForm from '~src/shared/form/translatableSectionForm/TranslatableSectionForm';
import { SectionOpt, TranslatableField } from '~src/shared/form/translatableSectionForm/interfaces';
import { composeValidators } from '~src/shared/reactFinalForm/validators';

import { BtnRow } from './topicQuestionAdd.sc';

interface StateProps {
  loadingAddQuestion: boolean;
  addQuestionSections: SectionOpt[];
}

interface DefaultProps {
  loadingAddQuestion: boolean;
}

interface OwnProps {
  topic: Topic;
  addTopicQuestion(topicId: string, payload: QuestionSubmit): ApiReduxResp;
}

type Props = OwnProps & StateProps & WithTranslation;

interface State {
  showAddBox: boolean;
}

class TopicQuestionAdd extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    loadingAddQuestion: false,
  };

  state = {
    showAddBox: false,
  };

  onSubmit = (payload: Record<string, any>) => {
    const tmpPayload = { ...payload, isCommentEnabled: !payload.isCommentEnabled };
    if (!tmpPayload.selfTitle) {
      tmpPayload.selfTitle = tmpPayload.title;
    }
    return this.props.addTopicQuestion(this.props.topic.id, tmpPayload).then((response) => {
      if (!response.error) {
        this.toggleAddBox();
      } else {
        logger.info('Error creating Question', {
          error: new Error('Error creating Question'),
          info: {
            componentStack: 'TopicQuestionAdd.jsx',
          },
        });
      }
      return response;
    });
  };

  toggleAddBox = () => {
    this.setState((prevState: State) => ({ showAddBox: !prevState.showAddBox }));
  };

  render() {
    const { t } = this.props;

    return (
      <>
        {!this.state.showAddBox ? (
          <BtnRow>
            <PrimaryButton onClick={this.toggleAddBox}>
              <span data-translation="x.topics.details.question.add.button">
                {t('x.topics.details.question.add.button', {
                  topicName: translateContent(this.props.topic.name),
                })}
              </span>
            </PrimaryButton>
          </BtnRow>
        ) : (
          <>
            <TranslatableSectionForm
              t={t}
              form="addAttributeOption"
              sections={this.props.addQuestionSections}
              titleKey="x.topics.details.question.add.title"
              tooltip={{
                key: 'x.topics.details.question.add.info_box.content',
                txt: t('x.topics.details.question.add.info_box.content'),
              }}
              loadingSave={this.props.loadingAddQuestion}
              onSubmit={this.onSubmit}
              onCancel={this.toggleAddBox}
              inContainer
            />
          </>
        )}
      </>
    );
  }
}

const mapStateToProps = ({ topics }: StoreI, { topic }: OwnProps) => {
  let nameLabel = 'x.topics.details.question.add.name.placeholder';
  let addQuestionFields: TranslatableField[] = [];
  if (topic.subject === TopicSubject.People) {
    nameLabel = 'x.topics.details.question.add.assesment_by_others.placeholder';
    addQuestionFields = [
      {
        name: 'selfTitle',
        label: 'x.topics.details.question.add.self_assessment.placeholder',
        validator: validatorMaxLengthLink500,
        translationValidator: validatorMaxLengthLink500,
        type: 'textArea',
      },
    ];
  }
  addQuestionFields = [
    {
      name: 'title',
      label: nameLabel,
      validator: composeValidators([validatorRequired, validatorMaxLengthLink500]),
      translationValidator: validatorMaxLengthLink500,
      type: 'textArea',
    },
    ...addQuestionFields,
    {
      name: 'description',
      label: 'x.topics.details.question.add.description.placeholder',
      validator: validatorMaxLengthLink1000,
      translationValidator: validatorMaxLengthLink1000,
      type: 'textArea' as const,
    },
    {
      name: 'isCommentEnabled',
      label: 'x.topics.details.question.add.is_comment_enabled.placeholder',
      type: 'checkbox' as const,
    },
  ];

  const improveFields = [
    {
      name: 'howToImprove',
      label: 'x.topics.details.question.improve.manager',
      type: 'textArea' as const,
    },
    {
      name: 'howToImproveSelf',
      label: 'x.topics.details.question.improve.own',
      type: 'textArea' as const,
    },
  ];

  const addQuestionSections = [
    {
      name: 'info',
      fields: addQuestionFields,
    },
    {
      name: 'improve',
      titleKey: 'x.topics.details.section.improve.label',
      descriptionKey: 'x.topics.details.section.improve.descr',
      fields: improveFields,
    },
  ];
  return {
    loadingAddQuestion: topics.loadingAddQuestion,
    addQuestionSections,
  };
};

const TopicQuestionAddC = connect(mapStateToProps)(TopicQuestionAdd);
const TopicQuestionAddT = withTranslation()(TopicQuestionAddC);

export default TopicQuestionAddT;
